import { InputField } from "@/components/InputField";
import { Text } from "@/components/Text";
import { ToastMessage } from "@/components/ToastMessage";
import { Button, Card, Collapsible, CollapsibleContent } from "@/components/ui";
import { useRequestTokens, useTokensEligibility } from "@/hooks/api/useFaucet";
import { getMaskedAddress } from "@/lib/utils";
import { debounce as _debounce } from "lodash";
import { InfoIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { isAddress } from "viem";
import { SupportedTokens } from "./SupportedTokens";

import Script from "next/script";

// Add TypeScript declarations for Turnstile
declare global {
  interface Window {
    onTurnstileSuccess: (token: string) => void;
    onTurnstileExpired: () => void;
    onTurnstileError: (error: number) => void;
    onTurnstileTimeout: () => void;
    turnstile?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export function FaucetForm() {
  const [tokens, setTokens] = useState<string[]>(["ETH"]);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  const [inputValue, setInputValue] = useState<string>("");
  const [inputError, setInputError] = useState<string>("");
  const [addressValue, setAddressValue] = useState<string>("");

  const {
    data: eligibility,
    isLoading,
    isSuccess,
  } = useTokensEligibility({
    address: addressValue,
    tokens: tokens.toString(),
  });

  const { mutateAsync: requestToken } = useRequestTokens();

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const isDripDisabled =
    !turnstileToken ||
    !addressValue ||
    tokens.length === 0 ||
    eligibility?.summary.eligible === 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(_debounce(handleOnAddressChange, 500), []);

  async function onRequestToken() {
    await requestToken({
      address: addressValue,
      turnstileToken,
      tokens,
    });
    console.log("mutate completed");

    onExpireCallback();
  }

  function handleOnAddressChange(value: string) {
    if (value) {
      if (isAddress(value)) {
        setAddressValue(value);
        setInputError("");
      } else {
        setAddressValue("");
        setInputError(
          "Please enter a valid Ethereum address (0x followed by 40 hexadecimal characters)"
        );
      }
    } else {
      setInputError("");
      setAddressValue("");
    }
  }

  function onErrorCallback(error: number) {
    console.log("verification failed!", error);
    setTurnstileToken("");

    const message =
      error === 110200
        ? "Unknown domain: Domain not allowed."
        : "Please contact Site Administrator";

    toast(
      <ToastMessage
        title="CloudFlare Verification Failed!"
        content={message}
      />,
      { type: "error" }
    );
  }

  function onTimeOutCallback() {
    console.log("verification timeout!");
    setTurnstileToken("");
    toast(
      <ToastMessage
        title="Verification Failed!"
        content="Verification has timed out."
      />,
      { type: "error" }
    );
  }

  function onExpireCallback() {
    if (window && window.turnstile) {
      console.log("reset token");
      window.turnstile.reset("#cf-turnstile");
      setTurnstileToken("");
    }
  }

  useEffect(() => {
    if (isMounted) {
      window.onTurnstileExpired = onExpireCallback;
      window.onTurnstileError = onErrorCallback;
      window.onTurnstileTimeout = onTimeOutCallback;
      window.onTurnstileSuccess = function () {
        if (window.turnstile && siteKey) {
          window.turnstile.render("#cf-turnstile", {
            sitekey: siteKey,
            callback: function (token: string) {
              console.log("verification success!!");
              setTurnstileToken(token);
            },
            "expired-callback": onExpireCallback,
            "error-callback": onErrorCallback,
            "timeout-callback": onTimeOutCallback,
          });
        }
      };
    }

    return () => {
      // Cleanup: todo - add
      //  delete  window.onTurnstileSuccess;
      //  delete window.onTurnstileExpired;
      //  delete window.onTurnstileError;
    };
  }, [isMounted, siteKey]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Card>
      {/* Load the Cloudflare Turnstile script */}
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileSuccess"
        strategy="afterInteractive"
      />

      <Text variant="heading">RISE Faucet</Text>
      <div className="pt-2 flex flex-col gap-4 justify-between">
        <div className="flex flex-col gap-4">
          <InputField
            required
            label="Wallet Address:"
            value={inputValue}
            onChange={(event) => {
              const value = event.target.value;
              setInputValue(value);
              debounceFn(value);
            }}
          />
          <div>
            <SupportedTokens tokens={tokens} setTokens={setTokens} />

            <Collapsible className="group/collapsible" open={!!inputError}>
              <CollapsibleContent data-state={!inputError ? "closed" : "open"}>
                <Text className="text-red-500 p-1">{inputError}</Text>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible className="group/collapsible" open={!!addressValue}>
              <CollapsibleContent
                asChild
                data-state={addressValue || !isSuccess ? "open" : "closed"}
              >
                <Card className="flex flex-row items-center md:gap-2 rounded-md  p-4 pt-2 mt-2">
                  <InfoIcon className="@max-2xs:hidden" size={20} />
                  <div>
                    <Text className="mt-1">
                      <span className="font-medium pr-2">Address:</span>
                      {getMaskedAddress(addressValue ?? "")}
                    </Text>
                    {!isLoading ? (
                      <Text
                        data-available={!!eligibility?.summary?.eligible}
                        className="data-[available=false]:text-red-400 data-[available=true]:text-green-400"
                      >
                        {`${
                          eligibility?.summary?.eligible
                            ? `${tokens} is available for request.`
                            : `${tokens} is not available for request!`
                        }`}
                      </Text>
                    ) : (
                      <Text>Checking eligibility...</Text>
                    )}
                  </div>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <div className="flex flex-wrap gap-2">
            <div
              id="cf-turnstile"
              className="cf-turnstile bg-accent rounded-md overflow-hidden h-[63px] flex-1"
              data-sitekey={siteKey}
              data-callback="onTurnstileSuccess"
              data-expired-callback="onTurnstileExpired"
              data-error-callback="onTurnstileError"
              data-size="flexible"
              data-theme="dark"
            />
            <Button
              disabled={isDripDisabled}
              className="h-16 px-6 text-md place-self-center max-sm:w-full"
              onClick={onRequestToken}
            >
              Request Token
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
